'use client';

import React, { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import type { Product } from '@/components/ProductsSection';

interface ProductModalProps {
  product: Product;
  onClose: () => void;
}

export default function ProductModal({ product, onClose }: ProductModalProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} role="dialog" aria-modal="true" aria-label={`${product.name} product details`}>
      <div className="modal-content">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold tracking-widest uppercase text-secondary bg-green-50 px-3 py-1 rounded-full">
              {product.category}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Close product detail"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="grid sm:grid-cols-2 gap-6 items-start">
            {/* Image */}
            <div className="bg-gradient-to-b from-slate-50 to-blue-50 rounded-xl flex items-center justify-center p-6">
              <AppImage
                src={product.image}
                alt={`${product.name} pharmaceutical product creative`}
                width={400}
                height={400}
                className="object-contain w-full h-auto max-h-[320px]"
                sizes="340px"
                priority
              />
            </div>

            {/* Details */}
            <div className="space-y-5">
              <div>
                <h2 className="font-display text-2xl text-foreground">{product.name}</h2>
                {product.form && (
                  <p className="text-sm text-muted-foreground italic mt-1">{product.form}</p>
                )}
              </div>

              <div className="space-y-3">
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Composition</p>
                  <p className="text-foreground font-medium text-sm leading-relaxed">{product.composition}</p>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Therapeutic Category</p>
                  <p className="text-foreground font-medium text-sm">{product.category}</p>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Brand</p>
                  <p className="text-foreground font-medium text-sm">EWIN Lifescience</p>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="disclaimer-box">
            <strong className="font-semibold">Disclaimer:</strong> Product information is provided for informational purposes. Medicines should be used only as directed by a qualified healthcare professional.
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="pharma-btn-secondary flex-1 justify-center"
          >
            ← Back to Products
          </button>
          <a href="#contact" onClick={onClose} className="pharma-btn-primary flex-1 justify-center">
            Enquire Now
          </a>
        </div>
      </div>
    </div>
  );
}